/**
 * MiniCalendar Component
 *
 * Month calendar view showing tasks on specific dates
 * Used in AdminDashboard and AdminProjectDetail
 *
 * @component
 */

import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./MiniCalendar.css";

/**
 * MiniCalendar Component
 * @param {Object} props - Component props
 * @param {Date} props.selectedDate - Currently selected date
 * @param {Function} props.onDateSelect - Date selection handler
 * @param {Object} props.tasksForDate - Object mapping date strings (YYYY-MM-DD) to task counts
 */
const MiniCalendar = ({ selectedDate, onDateSelect, tasksForDate = {} }) => {
  // Get current month and year from selected date
  const currentMonth = selectedDate.getMonth();
  const currentYear = selectedDate.getFullYear();

  // Get first day of month and number of days in month
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  // Get previous month days
  const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
  const prevMonthDays = Array.from(
    { length: startingDayOfWeek },
    (_, i) => prevMonthLastDay - startingDayOfWeek + i + 1,
  );

  // Get next month days (to fill remaining grid)
  const totalCells = Math.ceil((daysInMonth + startingDayOfWeek) / 7) * 7;
  const nextMonthDays = Array.from(
    { length: totalCells - (daysInMonth + startingDayOfWeek) },
    (_, i) => i + 1,
  );

  // Handle month navigation
  const goToPreviousMonth = () => {
    const newDate = new Date(currentYear, currentMonth - 1, 1);
    onDateSelect(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentYear, currentMonth + 1, 1);
    onDateSelect(newDate);
  };

  // Get task count for a specific date
  const getTaskCountForDate = (year, month, day) => {
    const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(
      day,
    ).padStart(2, "0")}`;
    return tasksForDate[dateString] || 0;
  };

  // Check if date is selected
  const isDateSelected = (year, month, day) => {
    return (
      selectedDate.getFullYear() === year &&
      selectedDate.getMonth() === month &&
      selectedDate.getDate() === day
    );
  };

  // Check if date is today
  const isToday = (year, month, day) => {
    const today = new Date();
    return (
      today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === day
    );
  };

  // Month name
  const monthName = new Date(currentYear, currentMonth).toLocaleString(
    "en-US",
    {
      month: "long",
      year: "numeric",
    },
  );

  return (
    <div className="mini-calendar">
      {/* Header with Month/Year and Navigation */}
      <div className="mini-calendar-header">
        <button
          className="mini-calendar-nav-btn"
          onClick={goToPreviousMonth}
          aria-label="Previous month"
        >
          <FaChevronLeft />
        </button>
        <h3>{monthName}</h3>
        <button
          className="mini-calendar-nav-btn"
          onClick={goToNextMonth}
          aria-label="Next month"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Day Headers */}
      <div className="mini-calendar-grid">
        <div className="mini-calendar-day-header">Sun</div>
        <div className="mini-calendar-day-header">Mon</div>
        <div className="mini-calendar-day-header">Tue</div>
        <div className="mini-calendar-day-header">Wed</div>
        <div className="mini-calendar-day-header">Thu</div>
        <div className="mini-calendar-day-header">Fri</div>
        <div className="mini-calendar-day-header">Sat</div>

        {/* Previous Month Days */}
        {prevMonthDays.map((day, index) => (
          <button
            key={`prev-${index}`}
            className="mini-calendar-day mini-calendar-day-other"
            onClick={() => {
              const newDate = new Date(currentYear, currentMonth - 1, day);
              onDateSelect(newDate);
            }}
          >
            <span className="mini-calendar-day-number">{day}</span>
          </button>
        ))}

        {/* Current Month Days */}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const taskCount = getTaskCountForDate(currentYear, currentMonth, day);
          const selected = isDateSelected(currentYear, currentMonth, day);
          const today = isToday(currentYear, currentMonth, day);

          return (
            <button
              key={day}
              className={`mini-calendar-day ${
                selected ? "mini-calendar-day-selected" : ""
              } ${today ? "mini-calendar-day-today" : ""} ${
                taskCount > 0 ? "mini-calendar-day-has-tasks" : ""
              }`}
              onClick={() => {
                const newDate = new Date(currentYear, currentMonth, day);
                onDateSelect(newDate);
              }}
            >
              <span className="mini-calendar-day-number">{day}</span>
              {taskCount > 0 && (
                <span className="mini-calendar-task-indicator">
                  {taskCount > 3 ? "•••" : "•".repeat(taskCount)}
                </span>
              )}
            </button>
          );
        })}

        {/* Next Month Days */}
        {nextMonthDays.map((day, index) => (
          <button
            key={`next-${index}`}
            className="mini-calendar-day mini-calendar-day-other"
            onClick={() => {
              const newDate = new Date(currentYear, currentMonth + 1, day);
              onDateSelect(newDate);
            }}
          >
            <span className="mini-calendar-day-number">{day}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MiniCalendar;
